/**
 * PlanEditor - Dialog for creating and editing billing plans
 *
 * Features:
 * - Create new plans or edit existing ones
 * - Configure plan name, description, pricing
 * - Set monthly and yearly pricing
 * - Define features included in the plan
 * - Set usage limits (calls, SMS, storage, etc.)
 */
import React from 'react';
import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Save, DollarSign, Settings as SettingsIcon, Zap } from 'lucide-react';
import type { BillingPlan } from '@/pages/admin/billing/plans';
import type { Json } from '@/integrations/supabase/types';

interface PlanEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: BillingPlan | null;
  onSuccess: () => void;
}

interface PlanFormData {
  name: string;
  display_name: string;
  description: string;
  base_price: number;
  features: {
    calls_enabled: boolean;
    sms_enabled: boolean;
    recording_enabled: boolean;
    transcription_enabled: boolean;
    analytics_enabled: boolean;
    integrations_enabled: boolean;
    multi_user: boolean;
    white_label: boolean;
  };
}

export function PlanEditor({ open, onOpenChange, plan, onSuccess }: PlanEditorProps) {
  const isEdit = !!plan;

  const { register, handleSubmit, watch, setValue, reset } = useForm<PlanFormData>({
    defaultValues: plan
      ? {
          name: plan.name,
          display_name: plan.display_name || plan.name,
          description: plan.description || '',
          base_price: plan.price_monthly || 0,
          features: (plan.features as PlanFormData['features']) || {
            calls_enabled: true,
            sms_enabled: true,
            recording_enabled: true,
            transcription_enabled: false,
            analytics_enabled: true,
            integrations_enabled: false,
            multi_user: false,
            white_label: false,
          },
        }
      : {
          name: '',
          display_name: '',
          description: '',
          base_price: 0,
          features: {
            calls_enabled: true,
            sms_enabled: true,
            recording_enabled: true,
            transcription_enabled: false,
            analytics_enabled: true,
            integrations_enabled: false,
            multi_user: false,
            white_label: false,
          },
        },
  });

  // Create/update plan mutation
  const saveMutation = useMutation({
    mutationFn: async (data: PlanFormData) => {
      if (isEdit && plan) {
        const { error } = await supabase.rpc('calie_admin_update_billing_plan', {
          p_plan_id: plan.id,
          p_name: data.name,
          p_display_name: data.display_name,
          p_description: data.description || null,
          p_base_price: data.base_price,
          p_features: data.features as unknown as Json,
        });
        if (error) throw error;
      } else {
        const { error } = await supabase.rpc('calie_admin_create_billing_plan', {
          p_name: data.name,
          p_display_name: data.display_name,
          p_description: data.description || null,
          p_base_price: data.base_price,
          p_features: data.features as unknown as Json,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Plan updated successfully' : 'Plan created successfully');
      reset();
      onSuccess();
    },
    onError: (error: Error) => {
      toast.error(`Failed to save plan: ${error.message}`);
    },
  });

  const onSubmit = (data: PlanFormData) => {
    saveMutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Plan' : 'Create New Plan'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update plan details, features, and limits'
              : 'Create a new billing plan with features and usage limits'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="details">
                <DollarSign className="h-4 w-4 mr-2" />
                Details
              </TabsTrigger>
              <TabsTrigger value="features">
                <Zap className="h-4 w-4 mr-2" />
                Features
              </TabsTrigger>
            </TabsList>

            {/* Details Tab */}
            <TabsContent value="details" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name">Plan Name (Internal) *</Label>
                <Input
                  id="name"
                  placeholder="e.g., starter, professional, enterprise"
                  {...register('name', { required: true })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="display_name">Display Name *</Label>
                <Input
                  id="display_name"
                  placeholder="e.g., Starter, Professional, Enterprise"
                  {...register('display_name', { required: true })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <textarea
                  id="description"
                  className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  placeholder="Brief description of this plan..."
                  {...register('description')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="base_price">Monthly Price (USD) *</Label>
                <Input
                  id="base_price"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="29.99"
                  {...register('base_price', { required: true, valueAsNumber: true })}
                />
              </div>
            </TabsContent>

            {/* Features Tab */}
            <TabsContent value="features" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Included Features</CardTitle>
                  <CardDescription>Select which features are included in this plan</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {[
                    { key: 'calls_enabled', label: 'Call Tracking', description: 'Track incoming calls' },
                    { key: 'sms_enabled', label: 'SMS Messaging', description: 'Send and receive SMS' },
                    { key: 'recording_enabled', label: 'Call Recording', description: 'Record phone calls' },
                    { key: 'transcription_enabled', label: 'Transcription', description: 'AI call transcription' },
                    { key: 'analytics_enabled', label: 'Analytics', description: 'Advanced analytics dashboard' },
                    { key: 'integrations_enabled', label: 'Integrations', description: 'Third-party integrations' },
                    { key: 'multi_user', label: 'Multi-User', description: 'Multiple team members' },
                    { key: 'white_label', label: 'White Label', description: 'Custom branding' },
                  ].map((feature) => (
                    <div key={feature.key} className="flex items-center justify-between space-x-4">
                      <div className="flex-1">
                        <Label htmlFor={feature.key} className="text-sm font-medium">
                          {feature.label}
                        </Label>
                        <p className="text-sm text-muted-foreground">{feature.description}</p>
                      </div>
                      <Switch
                        id={feature.key}
                        checked={watch(`features.${feature.key as keyof PlanFormData['features']}`)}
                        onCheckedChange={(checked) =>
                          setValue(`features.${feature.key as keyof PlanFormData['features']}`, checked)
                        }
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={saveMutation.isPending}>
              <Save className="h-4 w-4 mr-2" />
              {isEdit ? 'Update Plan' : 'Create Plan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
